/* The model sheet, second pass: the approved "Model Sheet Redesign".

   fx-sheet.js still does the heavy lifting (two columns, figures, preselected
   year, accordions moved into the right column). This file sits on top of
   that finished grid and changes four things, all by adding nodes beside the
   extension's own and hiding the ones they replace, never by recreating a
   control the extension listens to:

   1. LEFT COLUMN READS AS ONE STORY. A subtitle under the name (range, type,
      years) and one plain line under the figures in place of the "How to
      identify it" list: the four facts and the tell-apart sentence, joined.

   2. THE YEAR IS A ROW OF BUTTONS, NOT A DROPDOWN. The extension's select is
      still the source of truth: a button sets its value and fires the same
      input and change events a person choosing from the list would, so
      Find Parts arms exactly as before. The select is hidden, not removed.
      "I don't know my year" stays, as a quiet link under the buttons.

   3. ONE "FULL SPECIFICATIONS" ROW INSTEAD OF THREE ACCORDIONS. Clicking it
      swaps the right column to the specifications, every group open as a
      plain list, with a link back to the year. The red button does not move.
      On a phone it is the same swap inside the bottom sheet.

   4. ONE RED BUTTON. "Your selection" and "Back to the line-up" go (the
      close button is still there). The button says what it will do:
      "Find parts for 2026 Super Sport".

   30 Sep, on the phone review:
   - The subtitle under the name is gone. Range, type and years are now part
     of one description paragraph under the figures (descriptionFor).
   - No year is picked for you, so the hint that said one was is gone, and
     "I don't know my year" is gone. The question reads "Select your year
     model to continue" and the red button stays grey until a year is chosen.

   Every write is compared first: this runs on a body observer and a slow
   interval, and fx-ext-vinchip.js re-measures the VIN row on every body
   mutation. */
(function () {
  'use strict';

  var ARROW =
    '<svg class="fx-ms__arrow" width="16" height="14" viewBox="0 0 14 12" fill="none" aria-hidden="true">' +
    '<path d="M1 6h11M8 2l4 4-4 4" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var CHEV =
    '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
    '<path d="M6 3.5 10.5 8 6 12.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var BACK =
    '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
    '<path d="M10 3.5 5.5 8 10 12.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  var timer = 0;

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function setText(node, text) {
    if (node && node.textContent !== text) node.textContent = text;
  }

  function selectorApi() {
    var host = document.querySelector('vin-search-modal');
    try { return host && typeof host.getSelectorApi === 'function' ? host.getSelectorApi() : null; }
    catch (e) { return null; }
  }

  /* ---- the description ----
     One paragraph in a fixed shape, approved by Reuben on 30 Sep:
       "The Super Sport is a tandem axle lowering trailer with an aluminum
        chassis. It has an open deck and black fenders. It is part of the Sport
        Range of Futura trailers and is manufactured from 2020 to present."
     then the matrix's tell-apart sentence, because on the template alone six
     trailers read word for word the same apart from their names. Set TELL to
     false to drop that sentence.
     Every fact comes from the same matrix the line-up is painted from. A model
     missing a fact is described without it, never with a guess. */
  var TELL = true;

  /* 30 Sep, second phone review: the description is now short paragraphs with
     a blank line between them, and the copy is written out per model rather
     than assembled. The four Sport Range paragraphs are Reuben's wording,
     verbatim (Pro Sport, Super Sport, Super Tourer, Club Sport); the rest
     follow the same shape. A model missing from this list (a new one added
     to the matrix) still gets the assembled description below, so nothing
     ever renders empty. Paragraphs are joined with a blank line and the CSS
     shows it with white-space: pre-line. */
  var COPY = {
    'Super Sport': [
      'The Super Sport is a tandem axle lowering trailer with an aluminum chassis.',
      'It has an open deck and black fenders. It is part of the Sport Range of Futura trailers and is manufactured from 2020 to present.',
      'The most popular Sport Range/4th Generation Futura Trailer.'
    ],
    'Pro Sport': [
      'The Pro Sport is a tandem axle lowering trailer with an aluminum chassis.',
      'It has an open deck and black fenders. It is part of the Sport Range of Futura trailers and is manufactured from 2020 to present.',
      'Three feet longer than a Super Sport.'
    ],
    'Club Sport': [
      'The Club Sport is a single axle lowering trailer with an aluminum chassis.',
      'It has an open deck and black fenders. It is part of the Sport Range of Futura trailers and is manufactured from 2020 to present.',
      'It is the only single-axle Sport Range trailer.'
    ],
    'Super Tourer': [
      'The Super Tourer is a tandem axle enclosed lowering trailer with an aluminum chassis.',
      'It has an enclosed body and black fenders.',
      'It is part of the Sport Range of Futura trailers and is manufactured from 2020 to present.',
      'It is the only enclosed Sport Range trailer. Composite vinyl body over a Pro Sport chassis.'
    ],
    'RT500': [
      'The RT500 is a tandem axle ramp trailer with an aluminum chassis.',
      'It has an open deck and black fenders. It is part of the Sport Range of Futura trailers and is manufactured from 2020 to present.',
      'Quick-release ramps stow under the deck.'
    ],
    'Tilt': [
      'The Tilt is a tandem axle tilt deck trailer with an aluminum chassis.',
      'It has an open deck and black fenders. It is part of the Sport Range of Futura trailers and is manufactured from 2020 to present.',
      'The deck tilts to 10 degrees instead of lowering, on a patented axle.'
    ],
    'SHIFTR 1883': [
      'The SHIFTR 1883 is a tandem axle lowering trailer with a steel chassis.',
      'It has an open deck and black fenders. It is part of the SHIFTR range and is manufactured from 2026 to present.',
      'It is the only steel chassis trailer we build. Lockable toolbox as standard.'
    ],
    'Single': [
      'The Single is a single axle lowering trailer with an aluminum chassis.',
      'It has an open deck and silver fenders. It is part of the Gen 3 range of Futura trailers and was manufactured from 2012 to 2020.',
      'It is the shortest Gen 3 car trailer.'
    ],
    'Tandem': [
      'The Tandem is a tandem axle lowering trailer with an aluminum chassis.',
      'It has an open deck and silver fenders. It is part of the Gen 3 range of Futura trailers and was manufactured from 2012 to 2020.',
      'Measure the deck: 16\'5" is a Tandem, 19\'8" is a Tandem Pro.'
    ],
    'Tandem Pro': [
      'The Tandem Pro is a tandem axle lowering trailer with an aluminum chassis.',
      'It has an open deck and silver fenders. It is part of the Gen 3 range of Futura trailers and was manufactured from 2012 to 2020.',
      'Three feet longer than a Tandem.'
    ],
    'Enclosed': [
      'The Enclosed is a tandem axle enclosed lowering trailer with an aluminum chassis.',
      'It has an enclosed body and silver fenders.',
      'It is part of the Gen 3 range of Futura trailers and was manufactured from 2012 to 2020.',
      'It is the only enclosed Gen 3 trailer. Full hard body over a Tandem Pro chassis.'
    ],
    'Supercar': [
      'The Supercar is a tandem axle lowering trailer with an aluminum chassis.',
      'It has an open deck and silver fenders. It is part of the Gen 3 range of Futura trailers and was manufactured from 2012 to 2020.',
      'Wider 81 inch deck for low, wide cars.'
    ],
    'Supercar Pro': [
      'The Supercar Pro is a tandem axle lowering trailer with an aluminum chassis.',
      'It has an open deck and silver fenders. It is part of the Gen 3 range of Futura trailers and was manufactured from 2012 to 2020.',
      'Wider 81 inch deck, three feet longer than a Supercar.'
    ],
    'Motorcycle': [
      'The Motorcycle is a single axle lowering trailer with an aluminum chassis.',
      'It has an open deck and silver fenders. It is part of the Gen 3 range of Futura trailers and was manufactured from 2012 to 2020.',
      'It is the smallest trailer we made, with a 10 foot deck and no brakes.'
    ]
  };
  var PARA = '\n\n';

  var KIND = {
    'Lowering Trailer': 'lowering trailer',
    'Lowering Trailer Enclosed': 'enclosed lowering trailer',
    'Ramp Trailer': 'ramp trailer',
    'Tilt Deck': 'tilt deck trailer'
  };

  function article(word) { return /^[aeiou]/i.test(word) ? 'an' : 'a'; }

  function descriptionFor(name) {
    if (COPY[name]) return COPY[name].join(PARA);
    var api = selectorApi();
    var m = null;
    try { m = api && api.modelByName ? api.modelByName(name) : null; } catch (e) { m = null; }
    if (!m) return '';
    var axle = m.axles ? m.axles + ' axle ' : '';
    var kind = KIND[m.type] || 'trailer';
    var chassis = m.chassis === 'aluminium' ? 'aluminum' : (m.chassis || '');
    var head = axle + kind;
    var s1 = 'The ' + m.name + ' is ' + article(head) + ' ' + head +
      (chassis ? ' with ' + article(chassis) + ' ' + chassis + ' chassis' : '') + '.';

    var has = [];
    if (m.body === 'enclosed') has.push('an enclosed body');
    else if (m.body === 'open') has.push('an open deck');
    if (m.fenders) has.push(m.fenders + ' fenders');
    var s2 = has.length ? 'It has ' + has.join(' and ') + '.' : '';

    var rid = m.ranges && m.ranges[0];
    var range = rid && api.matrix && api.matrix.ranges ? api.matrix.ranges[rid] : null;
    var where = '';
    if (m.brand === 'SHIFTR') where = 'the SHIFTR range';
    else if (range && range.label) where = 'the ' + range.label + (/range/i.test(range.label) ? '' : ' range') + ' of Futura trailers';
    var when = '';
    var years = [];
    try { years = rid && api.yearsFor ? api.yearsFor(rid) : []; } catch (e) { years = []; }
    var nums = (years || []).map(Number).filter(function (n) { return n > 0; });
    if (nums.length) {
      var lo = Math.min.apply(null, nums);
      var hi = Math.max.apply(null, nums);
      when = hi >= new Date().getFullYear()
        ? 'is manufactured from ' + lo + ' to present'
        : 'was manufactured from ' + lo + (hi !== lo ? ' to ' + hi : '');
    }
    var s3 = where ? 'It is part of ' + where + (when ? ' and ' + when : '') + '.' : '';

    var tell = TELL && m.tellApart ? String(m.tellApart).trim() : '';
    var mid = [s2, s3].filter(Boolean).join(' ');
    return [s1, mid, tell].filter(Boolean).join(PARA);
  }

  /* The old line, kept as the fallback if the matrix cannot be read:
     "Aluminum chassis, tandem axle, open deck, black fenders. <sentence>" */
  function lineFor(idpop) {
    if (!idpop) return '';
    var parts = [];
    var cells = idpop.querySelectorAll('.ident-idcell');
    for (var i = 0; i < cells.length; i++) {
      var lab = cells[i].querySelector('span');
      var val = cells[i].querySelector('b');
      var v = val ? val.textContent.trim() : '';
      if (!v) continue;
      var l = lab ? lab.textContent.trim().toLowerCase() : '';
      /* "Aluminum" reads better as "Aluminum chassis"; "Tandem axle" already
         says what it is. */
      if (l === 'chassis' && !/chassis/i.test(v)) v += ' chassis';
      parts.push(v);
    }
    var facts = '';
    if (parts.length) {
      facts = parts.join(', ').toLowerCase();
      facts = facts.charAt(0).toUpperCase() + facts.slice(1) + '.';
    }
    var tell = idpop.querySelector('.ident-tell');
    var sentence = tell ? tell.textContent.trim() : '';
    return (facts + (sentence ? ' ' + sentence : '')).trim();
  }

  function yearOptions(sel) {
    var out = [];
    for (var i = 0; i < sel.options.length; i++) {
      var v = sel.options[i].value;
      if (v && v !== 'any') out.push(v);
    }
    return out;
  }

  function choose(sel, value) {
    for (var i = 0; i < sel.options.length; i++) {
      if (sel.options[i].value === value) {
        if (sel.selectedIndex !== i) {
          sel.selectedIndex = i;
          sel.dispatchEvent(new Event('input', { bubbles: true }));
          sel.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }
    }
  }

  function setView(sheet, view) {
    if (sheet.getAttribute('data-fx-view') !== view) sheet.setAttribute('data-fx-view', view);
    var more = sheet.querySelector('.fx-ms__more');
    if (more) more.setAttribute('aria-expanded', view === 'specs' ? 'true' : 'false');
  }

  function upgrade(sheet) {
    var grid = sheet.querySelector('.fx-ms');
    if (!grid) return;
    var L = grid.querySelector('.fx-ms__l');
    var R = grid.querySelector('.fx-ms__r');
    var scroll = R && R.querySelector('.fx-ms__scroll');
    var name = L && L.querySelector('.fx-ms__name');
    var yearBlock = scroll && scroll.querySelector('.ident-sheet-year');
    var sel = yearBlock && yearBlock.querySelector('select.vin-filter-select');
    if (!L || !R || !scroll || !name || !yearBlock || !sel) return;

    if (!sheet.classList.contains('fx-v2')) sheet.classList.add('fx-v2');

    /* A fresh grid means a fresh open: start on the year, never on the specs
       view somebody left open last time. */
    if (!grid.fxV2) {
      grid.fxV2 = true;
      setView(sheet, 'year');
    }

    var modelName = (name.textContent || '').trim();

    /* ---- 1. left column ---- */
    var sub = L.querySelector('.fx-ms__sub');
    if (sub && sub.parentNode) sub.parentNode.removeChild(sub);

    var figs = L.querySelector('.fx-ms__figs');
    var line = L.querySelector('.fx-ms__line');
    if (!line && figs) {
      line = el('p', 'fx-ms__line');
      figs.insertAdjacentElement('afterend', line);
    }
    setText(line, descriptionFor(modelName) ||
      lineFor(L.querySelector('.fx-ms__id') || sheet.querySelector('.ident-idpop')));

    /* ---- 2. the year as buttons ---- */
    var q = yearBlock.querySelector('.fx-ms__q');
    var years = yearOptions(sel);
    var chips = yearBlock.querySelector('.fx-ms__chips');
    var sig = years.join(',');
    if (!chips || chips.getAttribute('data-sig') !== sig) {
      if (chips && chips.parentNode) chips.parentNode.removeChild(chips);
      chips = el('div', 'fx-ms__chips');
      chips.setAttribute('role', 'radiogroup');
      chips.setAttribute('aria-label', 'Model year');
      chips.setAttribute('data-sig', sig);
      years.forEach(function (y) {
        var b = el('button', 'fx-ms__chip', y);
        b.type = 'button';
        b.setAttribute('role', 'radio');
        b.setAttribute('data-fx-year', y);
        chips.appendChild(b);
      });
      if (q) q.insertAdjacentElement('afterend', chips);
      else yearBlock.insertBefore(chips, yearBlock.firstChild);
    }
    var hint = yearBlock.querySelector('.fx-ms__yhint');
    if (hint && hint.parentNode) hint.parentNode.removeChild(hint);
    var cur = sel.value;
    var btns = chips.querySelectorAll('.fx-ms__chip');
    for (var i = 0; i < btns.length; i++) {
      var on = btns[i].getAttribute('data-fx-year') === cur ? 'true' : 'false';
      if (btns[i].getAttribute('aria-checked') !== on) btns[i].setAttribute('aria-checked', on);
      var tab = on === 'true' || (!cur && i === 0) ? '0' : '-1';
      if (btns[i].getAttribute('tabindex') !== tab) btns[i].setAttribute('tabindex', tab);
    }
    var unsure = yearBlock.querySelector('.fx-ms__unsure');
    if (unsure && unsure.parentNode) unsure.parentNode.removeChild(unsure);

    /* ---- 3. one row for the specifications ---- */
    var specs = scroll.querySelector('.fx-ms__specs');
    var more = scroll.querySelector('.fx-ms__more');
    if (specs) {
      if (!more) {
        more = el('button', 'fx-ms__more');
        more.type = 'button';
        more.setAttribute('aria-expanded', 'false');
        more.innerHTML =
          '<span class="fx-ms__moret"><span class="fx-ms__moreh">Full specifications</span>' +
          '<span class="fx-ms__mores">Dimensions, weight ratings, running gear</span></span>' + CHEV;
        yearBlock.insertAdjacentElement('afterend', more);
      }
      if (!specs.querySelector('.fx-ms__back')) {
        var back = el('button', 'fx-ms__back');
        back.type = 'button';
        back.innerHTML = BACK + '<span>Back to year</span>';
        specs.insertBefore(back, specs.firstChild);
      }
      /* In the specifications view every group is open and reads as a list. */
      if (sheet.getAttribute('data-fx-view') === 'specs') {
        var ds = specs.querySelectorAll('details');
        for (var d = 0; d < ds.length; d++) if (!ds[d].open) ds[d].open = true;
      }
    } else if (more && more.parentNode) {
      more.parentNode.removeChild(more);
    }

    /* ---- 4. one red button ---- */
    var primary = R.querySelector('.fx-ms__foot .is-primary');
    if (primary) {
      var year = cur && cur !== 'any' ? cur + ' ' : '';
      var label = 'Find parts for ' + year + modelName;
      if (primary.getAttribute('data-fx-label') !== label) {
        primary.setAttribute('data-fx-label', label);
        primary.innerHTML = '';
        primary.appendChild(el('span', null, label));
        primary.insertAdjacentHTML('beforeend', ARROW);
      }
    }
  }

  function tick() {
    timer = 0;
    var sheets = document.querySelectorAll('.ident-sheet');
    for (var i = 0; i < sheets.length; i++) {
      try { upgrade(sheets[i]); } catch (e) {
        if (window.console && console.warn) console.warn('[fx-sheet2]', e);
      }
    }
  }

  function schedule() { if (!timer) timer = setTimeout(tick, 20); }

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var sheet = t.closest('.ident-sheet');
    if (!sheet) return;

    var chip = t.closest('.fx-ms__chip');
    var unsure = t.closest('.fx-ms__unsure');
    if (chip || unsure) {
      var sel = sheet.querySelector('select.vin-filter-select');
      if (sel) choose(sel, chip ? chip.getAttribute('data-fx-year') : 'any');
      schedule();
      return;
    }
    if (t.closest('.fx-ms__more')) {
      setView(sheet, 'specs');
      tick();
      var sp = sheet.querySelector('.fx-ms__specs');
      if (sp && sp.scrollIntoView) sp.scrollIntoView({ block: 'nearest' });
      var bk = sheet.querySelector('.fx-ms__back');
      if (bk) bk.focus({ preventScroll: true });
      return;
    }
    if (t.closest('.fx-ms__back')) {
      setView(sheet, 'year');
      var mo = sheet.querySelector('.fx-ms__more');
      if (mo) mo.focus({ preventScroll: true });
    }
  });

  /* Arrow keys move through the year buttons, as in any radio group. */
  document.addEventListener('keydown', function (e) {
    var chip = e.target && e.target.closest && e.target.closest('.fx-ms__chip');
    if (!chip) return;
    var dir = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
    if (!dir) return;
    var all = Array.prototype.slice.call(chip.parentNode.querySelectorAll('.fx-ms__chip'));
    var next = all[(all.indexOf(chip) + dir + all.length) % all.length];
    if (next) { e.preventDefault(); next.focus(); next.click(); }
  });

  function start() {
    tick();
    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
    setInterval(tick, 800);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
